/**
 * queries.js
 * ----------
 * Lookups over the local blog registry. These replace the former
 * `GET /api/blog/posts` and `GET /api/blog/posts/:slug` calls — post content is
 * part of the build, so nothing here touches the network.
 *
 * Ordering, prev/next and related-post selection intentionally mirror the logic
 * that used to live in `backend/routes/blog.js` so the rendered pages are
 * unchanged.
 */
import { blogPosts } from './index';

// Newest first. Array.prototype.sort is stable, so posts sharing a date keep
// their registry order — prev/next stays deterministic across builds.
const byDateDesc = (a, b) => new Date(b.date) - new Date(a.date);

export const getAllPosts = () => [...blogPosts].sort(byDateDesc);

export const getPostMeta = (slug) => blogPosts.find((p) => p.slug === slug) || null;

export const getCategories = () => [...new Set(blogPosts.map((p) => p.category))].sort();

export const getTags = () => [...new Set(blogPosts.flatMap((p) => p.tags || []))].sort();

/**
 * Loads a post's markdown body. Split into its own chunk per post by the
 * bundler, so the blog index doesn't carry every article's text.
 */
export const loadPostContent = async (slug) => {
  const mod = await import(`./posts/${slug}.js`);
  return mod.content || mod.default || '';
};

/** Adjacent posts in date order — `prev` is newer, `next` is older. */
export const getAdjacentPosts = (slug) => {
  const sorted = getAllPosts();
  const idx = sorted.findIndex((p) => p.slug === slug);
  if (idx === -1) return { prev: null, next: null };

  return {
    prev: idx > 0 ? sorted[idx - 1] : null,
    next: idx < sorted.length - 1 ? sorted[idx + 1] : null,
  };
};

/** Explicit `relatedSlugs` first, then same-category posts as filler. */
export const getRelatedPosts = (slug, limit = 2) => {
  const meta = getPostMeta(slug);
  if (!meta) return [];

  const explicit = (meta.relatedSlugs || [])
    .map(getPostMeta)
    .filter((p) => p && p.slug !== slug);

  if (explicit.length >= limit) return explicit.slice(0, limit);

  const filler = getAllPosts().filter(
    (p) =>
      p.category === meta.category &&
      p.slug !== slug &&
      !explicit.some((e) => e.slug === p.slug)
  );

  return [...explicit, ...filler].slice(0, limit);
};
